import { formatCivilDate, formatInstant } from '@/shared/format/date';
import { formatCents } from '@/shared/format/money';

// Os formatadores do legado que os datasets de bureau usam para montar texto, vindos de `credit-note`
// junto com o catálogo (que agora mora aqui). Cada um cita a função original; onde o legado devolve lixo
// para valor ausente, aqui sai `null` e quem chama mostra "N/D" (E8B-DEPARA-RELATORIO).

const HAS_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * `formatDate` (helpers/formats/DateFormat.ts:265): `moment(date).format('DD/MM/YYYY')`, no fuso do
 * navegador. Com offset, o dia do instante em São Paulo (o fuso de quem opera); sem offset, a data
 * como foi escrita.
 */
export function legacyDate(value: unknown, locale = 'pt-BR'): string | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const text = value.trim();
  if (text.includes('T') && HAS_OFFSET.test(text)) return formatInstant(text, { locale, time: false });
  return formatCivilDate(text, { locale }) ?? formatInstant(text, { locale, time: false });
}

/** `new Date(v).toLocaleString('pt-BR')` (data e hora) dos datasets. */
export function legacyDateTime(value: unknown, locale = 'pt-BR'): string | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  return formatInstant(value.trim(), { locale, seconds: true });
}

/** `formatMonthYear` (helpers/formats/DocumentNumber.ts:24-33): `202509` → `09/2025`; `09/2025` fica. */
export function legacyMonthYear(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  if (/^\d{2}\/\d{4}$/.test(value)) return value;
  if (!/^\d{6}$/.test(value)) return null;
  const month = Number(value.slice(4, 6));
  if (month < 1 || month > 12) return null;
  return `${value.slice(4, 6)}/${value.slice(0, 4)}`;
}

/** `formatCurrencyInCents` (helpers/formats/Currency.ts:32-46): centavos → BRL; vazio vira R$ 0,00. */
export function legacyCents(value: unknown, locale = 'pt-BR'): string {
  const number = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  if (typeof number !== 'number' || !Number.isFinite(number)) return formatCents(0, { locale });
  return formatCents(Math.round(number), { locale });
}

/** `maskCurrencyInCents` (Currency.ts:63-71): o valor já em reais. */
export function legacyReais(value: unknown, locale = 'pt-BR'): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || value === 0) return formatCents(0, { locale });
  return formatCents(Math.round(value * 100), { locale });
}

/**
 * `formatDocumentNumber` (DocumentNumber.ts:3-22): pontua pelo tamanho, **sem conferir o dígito**
 * (o documento que vem do bureau ou do empregador aparece como veio, D10). CNPJ alfanumérico com
 * zeros à esquerda até 14, como o legado.
 */
export function legacyDocument(value: unknown): string | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const text = String(value).trim();
  if (text === '' || text.includes('*')) return text === '' ? null : text;
  const cleaned = text.replace(/[^a-zA-Z0-9]/g, '');
  if (/[a-zA-Z]/.test(cleaned)) {
    if (cleaned.length > 14) return text;
    const padded = cleaned.toUpperCase().padStart(14, '0');
    return `${padded.slice(0, 2)}.${padded.slice(2, 5)}.${padded.slice(5, 8)}/${padded.slice(8, 12)}-${padded.slice(12, 14)}`;
  }
  if (cleaned.length <= 11) return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (cleaned.length <= 14) return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return text;
}

/** `formatPercentage(v, casas, multiplicar)` (Currency.ts:73-82): `toFixed` com vírgula; 0 → `0%`. */
export function legacyPercentage(value: unknown, places = 4, multiplyBy100 = true): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || value === 0) return '0%';
  return `${(multiplyBy100 ? value * 100 : value).toFixed(places).replace('.', ',')}%`;
}
